import Ionicons from "@expo/vector-icons/Ionicons";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import {
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from "react-native";

import { Modal } from "@/components/ui/app-modal";

import { useRouter } from "expo-router";

import { reflectionsApi } from "@/api";
import { ThemedText } from "@/components/themed-text";
import { BlurredBackdrop } from "@/components/ui/blurred-backdrop";
import { KeyboardAvoidingContainer } from "@/components/ui/keyboard-view";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import i18n from "@/i18n";
import {
  reflectionLimitReachedCopy,
  reflectionLimits,
} from "@/lib/reflection-limits";
import { useTrialOffer } from "@/lib/trial-offer";
import { useAuthStore } from "@/state/auth-store";
import type {
  ReflectionContext,
  ReflectionFeeling,
  ReflectionOutcome,
  ReflectionQuota,
  ReflectionSubject,
  ReflectionWhere,
} from "@/types";

type Option<T> = { value: T; label: string };

const OTHER_MAX_LENGTH = 24;

const WHERES: Option<ReflectionWhere>[] = [
  {
    value: "opncue",
    get label() {
      return i18n.t("growth.options.where.opncue");
    },
  },
  {
    value: "somewhere_else",
    get label() {
      return i18n.t("growth.options.where.somewhereElse");
    },
  },
];

const SUBJECTS: Option<ReflectionSubject>[] = [
  {
    value: "met_someone_new",
    get label() {
      return i18n.t("growth.options.subject.metSomeoneNew");
    },
  },
  {
    value: "started_conversation",
    get label() {
      return i18n.t("growth.options.subject.startedConversation");
    },
  },
  {
    value: "joined_in",
    get label() {
      return i18n.t("growth.options.subject.joinedIn");
    },
  },
  {
    value: "reached_out",
    get label() {
      return i18n.t("growth.options.subject.reachedOut");
    },
  },
  {
    value: "made_plan",
    get label() {
      return i18n.t("growth.options.subject.madePlan");
    },
  },
  {
    value: "tried_something_difficult",
    get label() {
      return i18n.t("growth.options.subject.triedSomethingDifficult");
    },
  },
  {
    value: "stepped_away",
    get label() {
      return i18n.t("growth.options.subject.steppedAway");
    },
  },
  {
    value: "custom",
    get label() {
      return i18n.t("growth.options.subject.other");
    },
  },
];

const OUTCOMES: Option<ReflectionOutcome>[] = [
  {
    value: "clicked",
    get label() {
      return i18n.t("growth.options.outcome.clicked");
    },
  },
  {
    value: "more_comfortable_than_expected",
    get label() {
      return i18n.t("growth.options.outcome.moreComfortable");
    },
  },
  {
    value: "nervous_but_stayed",
    get label() {
      return i18n.t("growth.options.outcome.nervousStayed");
    },
  },
  {
    value: "learned_about_them",
    get label() {
      return i18n.t("growth.options.outcome.learnedAboutThem");
    },
  },
  {
    value: "learned_about_myself",
    get label() {
      return i18n.t("growth.options.outcome.learnedAboutMyself");
    },
  },
  {
    value: "one_sided",
    get label() {
      return i18n.t("growth.options.outcome.oneSided");
    },
  },
  {
    value: "not_my_vibe",
    get label() {
      return i18n.t("growth.options.outcome.notMyVibe");
    },
  },
  {
    value: "nothing_in_particular",
    get label() {
      return i18n.t("growth.options.outcome.nothingInParticular");
    },
  },
  {
    value: "custom",
    get label() {
      return i18n.t("growth.options.outcome.other");
    },
  },
];

const FEELINGS: Option<ReflectionFeeling>[] = [
  {
    value: "energised",
    get label() {
      return i18n.t("growth.options.feeling.energised");
    },
  },
  {
    value: "proud",
    get label() {
      return i18n.t("growth.options.feeling.proud");
    },
  },
  {
    value: "comfortable",
    get label() {
      return i18n.t("growth.options.feeling.comfortable");
    },
  },
  {
    value: "curious",
    get label() {
      return i18n.t("growth.options.feeling.curious");
    },
  },
  {
    value: "unsure",
    get label() {
      return i18n.t("growth.options.feeling.unsure");
    },
  },
  {
    value: "awkward",
    get label() {
      return i18n.t("growth.options.feeling.awkward");
    },
  },
  {
    value: "drained",
    get label() {
      return i18n.t("growth.options.feeling.drained");
    },
  },
  {
    value: "disappointed",
    get label() {
      return i18n.t("growth.options.feeling.disappointed");
    },
  },
  {
    value: "custom",
    get label() {
      return i18n.t("growth.options.feeling.other");
    },
  },
];

type Values = {
  where: ReflectionWhere | null;
  subject: ReflectionSubject | null;
  feeling: ReflectionFeeling | null;
  outcome: ReflectionOutcome | null;
  subjectIsOther: boolean;
  feelingIsOther: boolean;
  outcomeIsOther: boolean;
  subjectOther: string;
  feelingOther: string;
  outcomeOther: string;
  note: string;
};

function createInitialValues(): Values {
  return {
    where: null,
    subject: null,
    feeling: null,
    outcome: null,
    subjectIsOther: false,
    feelingIsOther: false,
    outcomeIsOther: false,
    subjectOther: "",
    feelingOther: "",
    outcomeOther: "",
    note: "",
  };
}

export function ReflectionModal({
  visible,
  context,
  onClose,
  onUpgrade,
  onLogged,
}: {
  visible: boolean;
  context?: ReflectionContext;
  onClose: () => void;
  onUpgrade?: () => void;
  onLogged?: () => void;
}) {
  useTranslation();
  const c = useTheme();
  const router = useRouter();
  const plan = useAuthStore((s) => s.user?.plan);

  const [quota, setQuota] = useState<ReflectionQuota | null>(null);
  const noteLimit =
    quota?.noteMaxLength ?? reflectionLimits(plan).noteMaxLength;

  const steps = useMemo<string[]>(
    () => ["subject", "outcome", "feeling", "where", "note"],
    [],
  );

  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState<Values>(() =>
    createInitialValues(),
  );
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const trialOffer = useTrialOffer(
    "free_limit_reached",
    visible && limitReached && !done,
  );

  // The modal stays mounted (a global host), so re-initialise each time it
  // opens or the trigger context changes — otherwise a new prompt would reuse
  // the previous moment's answers.
  useEffect(() => {
    if (!visible) return;
    /* eslint-disable react-hooks/set-state-in-effect -- Opening this global modal intentionally starts a fresh session. */
    setStepIndex(0);
    setValues(createInitialValues());
    setSaving(false);
    setDone(false);
    setLimitReached(false);
    /* eslint-enable react-hooks/set-state-in-effect */

    // Check today's remaining reflections; if none, show the limit screen.
    let cancelled = false;
    void reflectionsApi
      .quota()
      .then((q) => {
        if (cancelled) return;
        setQuota(q);
        // `remaining === null` means unlimited (Plus) — never a limit.
        if (q.remaining !== null && q.remaining <= 0) setLimitReached(true);
      })
      .catch(() => {
        // Non-blocking — fall back to plan defaults and let the server enforce.
      });
    return () => {
      cancelled = true;
    };
  }, [visible, context]);

  function reset() {
    setStepIndex(0);
    setValues(createInitialValues());
    setSaving(false);
    setDone(false);
  }

  function close() {
    reset();
    onClose();
  }

  const step = steps[stepIndex];

  function advance() {
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  }

  function back() {
    if (stepIndex === 0) {
      close();
      return;
    }
    setStepIndex((i) => i - 1);
  }

  function pickSubject(value: ReflectionSubject) {
    if (value === "custom") {
      setValues((v) => ({
        ...v,
        subject: value,
        subjectIsOther: true,
        subjectOther: v.subjectOther,
      }));
      return;
    }
    setValues((v) => ({
      ...v,
      subject: value,
      subjectIsOther: false,
      subjectOther: "",
    }));
    advance();
  }

  function pickFeeling(value: ReflectionFeeling) {
    if (value === "custom") {
      setValues((v) => ({
        ...v,
        feeling: value,
        feelingIsOther: true,
        feelingOther: v.feelingOther,
      }));
      return;
    }
    setValues((v) => ({
      ...v,
      feeling: value,
      feelingIsOther: false,
      feelingOther: "",
    }));
    advance();
  }

  function pickOutcome(value: ReflectionOutcome) {
    if (value === "custom") {
      setValues((v) => ({
        ...v,
        outcome: value,
        outcomeIsOther: true,
        outcomeOther: v.outcomeOther,
      }));
      return;
    }
    setValues((v) => ({
      ...v,
      outcome: value,
      outcomeIsOther: false,
      outcomeOther: "",
    }));
    advance();
  }

  function pickWhere(value: ReflectionWhere) {
    setValues((v) => ({ ...v, where: value }));
    advance();
  }

  function setOtherValue(
    key: "subjectOther" | "feelingOther" | "outcomeOther",
    text: string,
  ) {
    setValues((v) => ({ ...v, [key]: text.slice(0, OTHER_MAX_LENGTH) }));
  }

  function buildReflectionNote() {
    const otherLines = [
      values.subjectIsOther && values.subjectOther.trim()
        ? i18n.t("growth.reflectionFlow.notePrefix.happened", {
            value: values.subjectOther.trim(),
          })
        : null,
      values.feelingIsOther && values.feelingOther.trim()
        ? i18n.t("growth.reflectionFlow.notePrefix.feeling", {
            value: values.feelingOther.trim(),
          })
        : null,
      values.outcomeIsOther && values.outcomeOther.trim()
        ? i18n.t("growth.reflectionFlow.notePrefix.stoodOut", {
            value: values.outcomeOther.trim(),
          })
        : null,
      values.note.trim() || null,
    ].filter((line): line is string => Boolean(line));

    return otherLines.join("\n").slice(0, noteLimit).trim();
  }

  async function submit() {
    if (
      !values.where ||
      !values.subject ||
      !values.outcome ||
      !values.feeling ||
      saving
    ) {
      return;
    }
    setSaving(true);
    try {
      await reflectionsApi.create({
        where: values.where,
        subject: values.subject,
        feeling: values.feeling,
        outcome: values.outcome,
        note: buildReflectionNote() || undefined,
      });
      setDone(true);
      onLogged?.();
    } catch (err) {
      setSaving(false);
      // The server (or mock) rejects once the daily quota is spent.
      if (
        err instanceof Error &&
        /reflection_quota_reached|reflections for today/i.test(err.message)
      ) {
        setLimitReached(true);
      }
    }
  }

  function goPricing() {
    onUpgrade?.() ?? onClose();
    router.push("/pricing");
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={close}
    >
      <View style={styles.screen}>
        <BlurredBackdrop
          accessibilityLabel={i18n.t("growth.reflectionFlow.closeA11y")}
          style={styles.backdrop}
          onPress={close}
        />

        <KeyboardAvoidingContainer style={styles.sheetWrap}>
          <View
            style={[
              styles.sheet,
              { backgroundColor: c.surface, borderColor: c.border },
            ]}
          >
            {limitReached && !done ? (
              <View style={styles.doneWrap}>
                <View
                  style={[
                    styles.doneIcon,
                    { backgroundColor: c.backgroundSelected },
                  ]}
                >
                  <Ionicons name="moon-outline" size={26} color={c.tint} />
                </View>
                <ThemedText variant="subtitle" center>
                  {reflectionLimitReachedCopy(quota?.plan ?? plan).title}
                </ThemedText>
                <ThemedText variant="body" tone="secondary" center>
                  {reflectionLimitReachedCopy(quota?.plan ?? plan).body}
                </ThemedText>
                {(quota?.plan ?? plan) !== "plus" ? (
                  <Pressable
                    onPress={goPricing}
                    style={[styles.primaryBtn, { backgroundColor: c.tint }]}
                  >
                    <ThemedText variant="bodyStrong" tone="onTint">
                      {/* The gate is where the trial is pitched — the user
                          just hit a Plus limit, which is the moment the
                          offer means something. It never starts here; the
                          checkout still asks. */}
                      {trialOffer
                        ? i18n.t("billing.trial.gateCta", {
                            days: trialOffer.days,
                          })
                        : i18n.t("growth.reflectionFlow.upgrade")}
                    </ThemedText>
                  </Pressable>
                ) : null}
                <Pressable onPress={close} hitSlop={8}>
                  <ThemedText variant="caption" tone="muted" center>
                    {i18n.t("growth.reflectionFlow.later")}
                  </ThemedText>
                </Pressable>
              </View>
            ) : done ? (
              <View style={styles.doneWrap}>
                <View
                  style={[styles.doneIcon, { backgroundColor: c.successTint }]}
                >
                  <Ionicons name="checkmark" size={26} color={c.success} />
                </View>
                <ThemedText variant="subtitle" center>
                  {i18n.t("growth.reflectionFlow.logged")}
                </ThemedText>
                <ThemedText variant="body" tone="secondary" center>
                  {i18n.t("growth.reflectionFlow.success")}
                </ThemedText>
                {quota && quota.remaining !== null && quota.remaining - 1 > 0 ? (
                  <ThemedText variant="caption" tone="muted" center>
                    {i18n.t("growth.reflectionFlow.remaining", {
                      count: quota.remaining - 1,
                    })}
                  </ThemedText>
                ) : null}
                <Pressable
                  onPress={close}
                  style={[styles.primaryBtn, { backgroundColor: c.tint }]}
                >
                  <ThemedText variant="bodyStrong" tone="onTint">
                    {i18n.t("common.done")}
                  </ThemedText>
                </Pressable>
              </View>
            ) : (
              <>
                <View style={styles.header}>
                  <Pressable
                    onPress={back}
                    hitSlop={10}
                    accessibilityLabel={i18n.t(
                      "growth.reflectionFlow.backA11y",
                    )}
                  >
                    <Ionicons
                      name={stepIndex === 0 ? "close" : "chevron-back"}
                      size={22}
                      color={c.textSecondary}
                    />
                  </Pressable>
                  <View style={styles.progress}>
                    {steps.map((s, i) => (
                      <View
                        key={s}
                        style={[
                          styles.progressDot,
                          {
                            backgroundColor: i <= stepIndex ? c.tint : c.border,
                          },
                        ]}
                      />
                    ))}
                  </View>
                  <View style={{ width: 22 }} />
                </View>

                {step === "subject" ? (
                  <StepList
                    title={i18n.t("growth.reflectionFlow.happened")}
                    options={SUBJECTS}
                    selected={values.subject}
                    onSelect={pickSubject}
                    footer={
                      values.subjectIsOther ? (
                        <OtherTextInput
                          value={values.subjectOther}
                          onChangeText={(text) =>
                            setOtherValue("subjectOther", text)
                          }
                          onContinue={advance}
                          c={c}
                        />
                      ) : null
                    }
                    c={c}
                  />
                ) : step === "outcome" ? (
                  <StepList
                    title={i18n.t("growth.reflectionFlow.stoodOut")}
                    options={OUTCOMES}
                    selected={values.outcome}
                    onSelect={pickOutcome}
                    footer={
                      values.outcomeIsOther ? (
                        <OtherTextInput
                          value={values.outcomeOther}
                          onChangeText={(text) =>
                            setOtherValue("outcomeOther", text)
                          }
                          onContinue={advance}
                          c={c}
                        />
                      ) : null
                    }
                    c={c}
                  />
                ) : step === "feeling" ? (
                  <StepList
                    title={i18n.t("growth.reflectionFlow.feelNow")}
                    options={FEELINGS}
                    selected={values.feeling}
                    onSelect={pickFeeling}
                    footer={
                      values.feelingIsOther ? (
                        <OtherTextInput
                          value={values.feelingOther}
                          onChangeText={(text) =>
                            setOtherValue("feelingOther", text)
                          }
                          onContinue={advance}
                          c={c}
                        />
                      ) : null
                    }
                    c={c}
                  />
                ) : step === "where" ? (
                  <StepList
                    title={i18n.t("growth.reflectionFlow.where")}
                    options={WHERES}
                    selected={values.where}
                    onSelect={pickWhere}
                    c={c}
                  />
                ) : (
                  <ScrollView
                    style={styles.noteStepScroll}
                    contentContainerStyle={styles.noteStepContent}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                  >
                    <View style={styles.stepBody}>
                      <ThemedText variant="subtitle">
                        {i18n.t("growth.reflectionFlow.remember")}
                      </ThemedText>
                      <ThemedText variant="caption" tone="secondary">
                        {i18n.t("growth.reflectionFlow.rememberHint")}
                      </ThemedText>
                      <TextInput
                        value={values.note}
                        onChangeText={(t) =>
                          setValues((v) => ({
                            ...v,
                            note: t.slice(0, noteLimit),
                          }))
                        }
                        placeholder={i18n.t(
                          "growth.reflectionFlow.optionalNote",
                        )}
                        placeholderTextColor={c.textMuted}
                        multiline
                        style={[
                          styles.note,
                          {
                            color: c.text,
                            backgroundColor: c.background,
                            borderColor: c.border,
                          },
                        ]}
                      />
                      <ThemedText
                        variant="caption"
                        tone="muted"
                        style={styles.count}
                      >
                        {values.note.length}/{noteLimit}
                      </ThemedText>
                      <Pressable
                        onPress={submit}
                        disabled={saving}
                        style={[
                          styles.primaryBtn,
                          {
                            backgroundColor: c.tint,
                            opacity: saving ? 0.7 : 1,
                          },
                        ]}
                      >
                        <ThemedText variant="bodyStrong" tone="onTint">
                          {saving
                            ? i18n.t("growth.reflectionFlow.saving")
                            : i18n.t("growth.reflectionFlow.log")}
                        </ThemedText>
                      </Pressable>
                    </View>
                  </ScrollView>
                )}
              </>
            )}
          </View>
        </KeyboardAvoidingContainer>
      </View>
    </Modal>
  );
}

function StepList<T extends string>({
  title,
  options,
  selected,
  onSelect,
  footer,
  c,
}: {
  title: string;
  options: Option<T>[];
  selected: T | null;
  onSelect: (value: T) => void;
  footer?: ReactNode;
  c: ReturnType<typeof useTheme>;
}) {
  return (
    <View style={styles.stepBody}>
      <ThemedText variant="subtitle">{title}</ThemedText>
      <ScrollView
        style={styles.optionScroll}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.options}>
          {options.map((option) => (
            <OptionRow
              key={option.value}
              label={option.label}
              active={selected === option.value}
              onPress={() => onSelect(option.value)}
              c={c}
            />
          ))}
        </View>
      </ScrollView>
      {footer}
    </View>
  );
}

function OtherTextInput({
  value,
  onChangeText,
  onContinue,
  c,
}: {
  value: string;
  onChangeText: (text: string) => void;
  onContinue: () => void;
  c: ReturnType<typeof useTheme>;
}) {
  const canContinue = value.trim().length > 0;

  function continueIfReady() {
    if (canContinue) onContinue();
  }

  return (
    <View style={styles.otherInputWrap}>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={i18n.t("growth.reflectionFlow.answerPlaceholder")}
        placeholderTextColor={c.textMuted}
        maxLength={OTHER_MAX_LENGTH}
        returnKeyType="done"
        onSubmitEditing={continueIfReady}
        style={[
          styles.otherInput,
          {
            color: c.text,
            backgroundColor: c.background,
            borderColor: c.border,
          },
        ]}
      />
      <Pressable
        onPress={continueIfReady}
        disabled={!canContinue}
        style={[
          styles.primaryBtn,
          {
            backgroundColor: c.tint,
            opacity: canContinue ? 1 : 0.45,
          },
        ]}
      >
        <ThemedText variant="bodyStrong" tone="onTint">
          {i18n.t("common.continue")}
        </ThemedText>
      </Pressable>
    </View>
  );
}

function OptionRow({
  label,
  active,
  onPress,
  c,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
  c: ReturnType<typeof useTheme>;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      onPress={onPress}
      style={[
        styles.option,
        {
          borderColor: active ? c.tint : c.border,
          backgroundColor: active ? c.backgroundSelected : c.surfaceAlt,
        },
      ]}
    >
      <ThemedText variant="body" tone={active ? "tint" : "default"}>
        {label}
      </ThemedText>
      {active ? (
        <Ionicons name="checkmark-circle" size={18} color={c.tint} />
      ) : (
        <Ionicons name="chevron-forward" size={16} color={c.textMuted} />
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(8, 15, 26, 0.5)",
  },
  sheetWrap: {
    flex: 1,
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: Spacing.lg,
    paddingVertical: 28,
  },
  sheet: {
    width: "100%",
    maxWidth: 420,
    maxHeight: "82%",
    padding: Spacing.lg,
    gap: Spacing.md,
    borderRadius: Radius.xl,
    borderWidth: 1,
    shadowColor: "#000000",
    shadowOpacity: 0.24,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 12 },
    elevation: 12,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  progress: { flexDirection: "row", gap: 6 },
  progressDot: { width: 18, height: 4, borderRadius: 2 },
  stepBody: { gap: Spacing.md },
  optionScroll: { maxHeight: 320 },
  options: { gap: Spacing.sm },
  option: {
    minHeight: 48,
    paddingHorizontal: Spacing.lg,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: Radius.lg,
    borderWidth: 1,
  },
  otherInputWrap: { gap: Spacing.sm },
  otherInput: {
    minHeight: 48,
    paddingHorizontal: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
    fontSize: 15,
  },
  noteStepScroll: { flexShrink: 1 },
  noteStepContent: { paddingBottom: Spacing.xs },
  note: {
    minHeight: 72,
    maxHeight: 120,
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: 1,
    fontSize: 15,
    textAlignVertical: "top",
  },
  count: { textAlign: "right", marginTop: -Spacing.sm },
  primaryBtn: {
    minHeight: 52,
    paddingHorizontal: Spacing.xl,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.lg,
    marginTop: Spacing.xs,
  },
  doneWrap: {
    alignItems: "center",
    gap: Spacing.md,
    paddingVertical: Spacing.md,
  },
  doneIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: "center",
    justifyContent: "center",
  },
});
